import { useCallback, useEffect, useMemo, useState } from 'react';

const TAB_ORDER_KEY = 'tab-order';

const localhost = window.location.hostname === 'localhost';

const getTabOrderKey = (objectType) => `${objectType}-${TAB_ORDER_KEY}`;

export const getSavedTabOrder = (objectType) => {
	if (!objectType) return [];
	const saved = localStorage.getItem(getTabOrderKey(objectType));
	if (!saved) return [];
	try {
		const order = JSON.parse(saved);
		return Array.isArray(order) ? order.filter((value) => typeof value === 'string') : [];
	} catch (error) {
		console.error('Error parsing saved tab order:', error);
		return [];
	}
};

export const saveTabOrder = ({ objectType, order = [] }) => {
	if (!objectType) return;
	localStorage.setItem(getTabOrderKey(objectType), JSON.stringify(order));
};

export const sortTabsByOrder = (tabs, order) => {
	if (!order?.length) return tabs;
	const remaining = new Map(tabs.map((tab) => [tab.value, tab]));
	const next = [];
	order.forEach((value) => {
		const tab = remaining.get(value);
		if (tab) {
			next.push(tab);
			remaining.delete(value);
		}
	});
	remaining.forEach((tab) => next.push(tab));
	return next;
};

// Without an alias the root tab has no URL of its own once moved, so keep it first.
const pinRootTab = (tabs) => {
	const rootTab = tabs.find((tab) => tab.value === '' && !tab.alias);
	if (!rootTab) return tabs;
	return [{ ...rootTab, locked: true }, ...tabs.filter((tab) => tab !== rootTab)];
};

export const useTabOrder = (objectType, tabs = []) => {
	const reorderable = Boolean(objectType);

	const [tabOrder, setTabOrder] = useState(() =>
		reorderable ? getSavedTabOrder(objectType) : [],
	);

	const displayTabs = useMemo(
		() => (reorderable ? pinRootTab(sortTabsByOrder(tabs, tabOrder)) : tabs),
		[reorderable, tabs, tabOrder],
	);

	const hasUnaliasedRootTab = reorderable && tabs.some((tab) => tab.value === '' && !tab.alias);

	useEffect(() => {
		if (!hasUnaliasedRootTab) return;
		const message = `${objectType} tabs: the root tab has no alias in app/tabs.js, so it can't be moved from first place.`;
		if (localhost) throw new Error(message);
		console.warn(message);
	}, [hasUnaliasedRootTab, objectType]);

	const persistOrder = useCallback(
		(order) => {
			let nextOrder = [];
			if (order.length) {
				const visibleValues = new Set(order);
				const remaining = [...order];
				// The reordered visible tabs fill the slots visible tabs held before, so hidden tabs keep their place.
				nextOrder = tabOrder.map((value) =>
					visibleValues.has(value) ? remaining.shift() : value,
				);
				// Visible tabs that weren't saved before go on the end.
				nextOrder.push(...remaining);
			}

			setTabOrder(nextOrder);
			saveTabOrder({ objectType, order: nextOrder });
		},
		[objectType, tabOrder],
	);

	return { reorderable, tabOrder, displayTabs, persistOrder };
};
